<template>
  <div>
    <!-- Menu bar with report buttons -->
    <ReportButtons
      :account_id="account_id"
      @report-created="handleReportCreated"
      @refresh="refreshSearch"
    />

    <!-- Search & Filter -->
    <div class="px-2 mt-3 mb-0 mobile-search-container">
      <v-text-field
        v-model="search"
        placeholder="Zoeken..."
        solo
        clearable
        density="compact"
        class="mobile-search-field"
        @click:clear="clearSearch"
        @update:model-value="handleSearchInput"
      >
        <template #append>
          <v-btn icon color="primary" variant="text" @click="openFilterWindow">
            <v-badge
              v-if="reportFilterStore.activeFiltersCount > 0"
              :content="reportFilterStore.activeFiltersCount.toString()"
              color="primary"
            >
              <v-icon>mdi-filter</v-icon>
            </v-badge>
            <v-icon v-else>mdi-filter</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <ReportFilterDialog
        :show="reportFilterStore.dialog.show"
        :account_id="account_id"
        @update:show="reportFilterStore.dialog.show = $event"
        @search="performSearch"
      />
    </div>

    <!-- Error message -->
    <v-alert v-if="error" type="error" variant="tonal" class="mx-2 mb-3">
      {{ error }}
    </v-alert>

    <!-- Reports Timeline -->
    <ReportTimeline
      :reports="displayedReports"
      :on-report-updated="handleReportUpdated"
    />

    <!-- Loading indicator -->
    <div v-if="currentlyLoading" class="text-center py-4">
      <v-progress-circular
        indeterminate
        color="primary"
        size="32"
      ></v-progress-circular>
      <p class="text-body-2 text-medium-emphasis mt-2 mb-0">
        Rapporten laden...
      </p>
    </div>

    <!-- Load more button (as fallback) -->
    <div v-if="!currentlyLoading && hasMoreData" class="text-center py-4">
      <v-btn
        variant="outlined"
        color="primary"
        :disabled="currentlyLoading"
        @click="loadMore"
      >
        Meer laden
      </v-btn>
    </div>

    <!-- Infinite loading trigger (invisible) -->
    <div
      v-if="!currentlyLoading && hasMoreData"
      ref="loadMoreTrigger"
      class="infinite-trigger"
      style="height: 20px"
    ></div>

    <!-- End of data message -->
    <div
      v-if="!currentlyLoading && !hasMoreData && displayedReports.length > 0"
      class="d-flex justify-center py-6"
    >
      <v-card
        class="text-center pa-6"
        variant="flat"
        color="grey-lighten-5"
        max-width="400"
        rounded="lg"
        elevation="0"
      >
        <div class="end-of-data-container">
          <div class="icon-wrapper mb-4">
            <v-avatar size="56" color="success-lighten-3" class="mb-2">
              <v-icon size="28" color="success-darken-1">
                mdi-check-circle-outline
              </v-icon>
            </v-avatar>
          </div>

          <div class="content-wrapper">
            <h4 class="text-h6 text-grey-darken-2 mb-2 font-weight-medium">
              Alle rapporten geladen
            </h4>
            <p
              class="text-body-2 text-grey-darken-1 mb-0"
              style="line-height: 1.4"
            >
              Er zijn geen rapporten meer om te laden.
              <br />
              <span class="font-weight-medium">
                {{ displayedReports.length }} rapport{{
                  displayedReports.length !== 1 ? "en" : ""
                }}
              </span>
              gevonden.
            </p>
          </div>
        </div>
      </v-card>
    </div>

    <!-- No reports found message -->
    <div
      v-if="!currentlyLoading && !hasMoreData && displayedReports.length === 0"
      class="d-flex justify-center py-8"
    >
      <v-card
        class="text-center pa-8"
        variant="outlined"
        color="surface"
        max-width="400"
        elevation="0"
      >
        <div class="no-reports-container">
          <div class="icon-wrapper mb-4">
            <v-avatar size="64" color="grey-lighten-4" class="mb-3">
              <v-icon size="32" color="grey-lighten-1">
                mdi-clipboard-text-search-outline
              </v-icon>
            </v-avatar>
          </div>

          <div class="content-wrapper">
            <h4 class="text-h6 text-grey-darken-2 mb-3 font-weight-medium">
              Geen rapporten gevonden
            </h4>
            <p
              class="text-body-2 text-grey-darken-1 mb-4"
              style="line-height: 1.4"
            >
              Er zijn nog geen rapporten voor deze account aangemaakt.
            </p>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import { useReportSearchStore } from "@/stores/reportSearchStore";
import { useReportFilterStore } from "@/stores/reportFilterStore";
import ReportTimeline from "../reports/ReportTimeline.vue";
import ReportButtonsRefactor from "../reports/ReportButtonsRefactor.vue";
import ReportFilterDialog from "../reports/ReportFilterDialog.vue";
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useIntersectionObserver } from "@vueuse/core";

// Simple debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default {
  name: "AccountReportsRefactor",
  components: {
    ReportTimeline,
    ReportButtons: ReportButtonsRefactor,
    ReportFilterDialog,
  },
  props: {
    account_id: {
      type: Number,
      required: true,
    },
    traitFilter: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const reportSearchStore = useReportSearchStore();
    const reportFilterStore = useReportFilterStore();

    // --- LOCAL STATE ---
    const error = ref(null);
    const loadMoreTrigger = ref(null);

    // Initialize filter store for this account
    reportFilterStore.initForAccount(props.account_id);

    // --- COMPUTED PROPERTIES ---

    // Search field bound to store
    const search = computed({
      get: () => reportFilterStore.filters.description_cont,
      set: (value) => reportFilterStore.setTextSearch(value),
    });

    // Check if we're in search/filter mode
    const hasActiveSearchOrFilters = computed(() => {
      return reportFilterStore.hasActiveFilters;
    });

    // Always use search store results (filtered by account_id)
    const displayedReports = computed(() => {
      return reportSearchStore.searchResults;
    });

    // Has more data logic - use store's shouldLoadMore that considers ActionCable sync
    const hasMoreData = computed(() => {
      return reportSearchStore.shouldLoadMore;
    });

    // Current loading state
    const currentlyLoading = computed(() => {
      return reportSearchStore.loading;
    });

    // --- METHODS ---

    // Build search query for this account
    const buildSearchQuery = () => {
      return reportFilterStore.searchQuery;
    };

    // Load reports for this account using search store
    const loadAccountReports = async () => {
      try {
        error.value = null;
        const query = buildSearchQuery();
        console.log("AccountReportsRefactor: loadAccountReports called with query:", query);
        if (query.contact_ids_cache_cont_any) {
          console.log("AccountReportsRefactor: Contact filter in query:", query.contact_ids_cache_cont_any);
        }
        await reportSearchStore.search({ q: query });
      } catch (err) {
        console.error("AccountReportsRefactor: Error loading reports:", err);
        error.value =
          err?.response?.data?.message ||
          err?.message ||
          "Er is een fout opgetreden bij het laden van rapporten.";
        reportSearchStore.hasMoreData = false;
      }
    };

    // Load more data using search store
    const loadMore = async () => {
      if (!reportSearchStore.shouldLoadMore) return;

      try {
        error.value = null;
        console.log("AccountReportsRefactor: Loading more reports");

        const query = buildSearchQuery();
        const results = await reportSearchStore.search({ q: query }, true); // append = true
        // Extra guard: if API returned no items, stop further attempts
        if (!results || (Array.isArray(results) && results.length === 0)) {
          reportSearchStore.hasMoreData = false;
        }
      } catch (err) {
        console.error("AccountReportsRefactor: Error loading more:", err);
        error.value =
          err?.response?.data?.message ||
          err?.message ||
          "Er is een fout opgetreden bij het laden van meer rapporten.";
        // Prevent further load-more attempts after an error
        reportSearchStore.hasMoreData = false;
      }
    };

    // Debounced search when user types
    const debouncedSearch = debounce(() => {
      loadAccountReports();
    }, 500);

    // Handle search input changes (including clearing)
    const handleSearchInput = (value) => {
      if (!value || value.trim() === "") {
        // Search was cleared - immediately reset
        clearSearch();
      } else {
        // Search has content - debounce the search
        debouncedSearch();
      }
    };

    // Refresh reports
    const refreshReports = () => {
      console.log("AccountReportsRefactor: Refreshing reports");
      error.value = null;
      console.log("AccountReportsRefactor: Store state before clear:", {
        dataLength: reportSearchStore.searchResults.length,
        loading: reportSearchStore.loading,
        hasMoreData: reportSearchStore.hasMoreData,
        shouldLoadMore: reportSearchStore.shouldLoadMore,
      });

      // Keep current filters active; clear only local results in search store
      reportSearchStore.data = [];
      reportSearchStore.currentOffset = 0;
      reportSearchStore.hasMoreData = true;

      console.log("AccountReportsRefactor: Store state after clear:", {
        dataLength: reportSearchStore.searchResults.length,
        loading: reportSearchStore.loading,
        hasMoreData: reportSearchStore.hasMoreData,
        shouldLoadMore: reportSearchStore.shouldLoadMore,
      });

      loadAccountReports();
    };

    // Clear search and filters
    const clearSearch = () => {
      error.value = null;
      reportFilterStore.clearAllFilters();
    };

    // Open filter window
    const openFilterWindow = () => {
      reportFilterStore.openDialog();
    };

    // Handle new report created
    const handleReportCreated = (report) => {
      console.log("AccountReportsRefactor: Report created:", report);
      console.log(
        "AccountReportsRefactor: Skipping handling new report refresh",
      );
      // Refresh to include the new report
      // refreshReports();
    };

    // Handle report updated
    const handleReportUpdated = (updatedReport) => {
      console.log("AccountReportsRefactor: Report updated:", updatedReport);
      // Refresh to include the updated report
      //refreshReports();
    };

    // Apply trait filter from parent component (legacy method - now handled by filter store)
    const applyTraitFilter = (traitData) => {
      error.value = null;
      reportFilterStore.applyTraitFilter(traitData.traitId);
    };

    // Intersection Observer for infinite loading
    const { stop: stopIntersectionObserver } = useIntersectionObserver(
      loadMoreTrigger,
      ([{ isIntersecting }]) => {
        if (isIntersecting && hasMoreData.value && !currentlyLoading.value) {
          console.log(
            "AccountReportsRefactor: Intersection detected, loading more...",
          );
          loadMore();
        }
      },
      {
        threshold: 0.1,
      },
    );

    // Lifecycle
    onMounted(async () => {
      console.log(
        `AccountReportsRefactor: Mounted for account ${props.account_id}`,
      );
      console.log(
        `AccountReportsRefactor: Store instance ID:`,
        reportSearchStore.$id,
      );
      console.log(`AccountReportsRefactor: Store initial state:`, {
        dataLength: reportSearchStore.searchResults.length,
        loading: reportSearchStore.loading,
      });

      // Initialize search store and load reports
      await reportSearchStore.init();
      await loadAccountReports();

      console.log(
        `AccountReportsRefactor: Found ${displayedReports.value.length} reports for this account`,
      );
    });

    onBeforeUnmount(() => {
      stopIntersectionObserver();
    });

    // Watch for account changes
    watch(
      () => props.account_id,
      (newAccountId) => {
        // Clear search store and reload for new account
        error.value = null;
        reportSearchStore.clear();
        reportFilterStore.initForAccount(newAccountId);
      },
    );

    // Watch for filter store changes (excluding search text which is handled separately)
    watch(
      () => reportFilterStore.filters,
      () => loadAccountReports(),
      { deep: true },
    );

    return {
      // State
      search,
      error,
      loadMoreTrigger,

      // Stores
      reportFilterStore,

      // Computed
      displayedReports,
      hasMoreData,
      currentlyLoading,
      hasActiveSearchOrFilters,

      // Methods
      loadAccountReports,
      handleSearchInput,
      loadMore,
      clearSearch,
      openFilterWindow,
      handleReportCreated,
      handleReportUpdated,
      refreshReports,
      applyTraitFilter,
      // Aliases for template compatibility
      refreshSearch: refreshReports,
      performSearch: loadAccountReports,
    };
  },
};
</script>

<style scoped>
.infinite-trigger {
  background: transparent;
}

/* 📱 MOBILE-ONLY: Reduce horizontal padding for more screen space */
@media (max-width: 599px) {
  .mobile-search-container {
    /* Reduce horizontal padding from 8px to 4px on mobile */
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  /* Ultra-compact search field for mobile */
  .mobile-search-field {
    /* Reduce overall height and font size */
    font-size: 0.875rem !important;
  }

  .mobile-search-field .v-field {
    /* Reduce field height significantly */
    min-height: 36px !important;
    height: 36px !important;
  }

  .mobile-search-field .v-field__input {
    /* Reduce input padding and line height */
    padding: 4px 8px !important;
    min-height: 28px !important;
    line-height: 1.2 !important;
    font-size: 0.875rem !important;
  }

  .mobile-search-field .v-field__prepend-inner,
  .mobile-search-field .v-field__append-inner {
    /* Compact icon areas */
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }

  .mobile-search-field .v-btn {
    /* Smaller filter button */
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
  }

  .mobile-search-field .v-icon {
    /* Smaller icons */
    font-size: 18px !important;
  }
}
</style>
